/**
 * Shared draggable tutorial panel – same behavior as Advanced calculator tutorial.
 * Wrap any tutorial content so the panel can be dragged around the viewport.
 */
import React, { useState, useRef, useEffect } from 'react';

const PANEL_WIDTH = 560;

const DraggableTutorialPanel = ({ children, onClose, title = 'Tutorial Guide' }) => {
  const [position, setPosition] = useState({ x: typeof window !== 'undefined' ? window.innerWidth - (PANEL_WIDTH + 20) : 400, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const panelRef = useRef(null);

  const handleMouseDown = (e) => {
    if (e.target.closest('.tutorial-content') || e.target.closest('button')) return;
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const newX = Math.max(0, Math.min(window.innerWidth - PANEL_WIDTH, e.clientX - dragOffset.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragOffset.y));
      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => setIsDragging(false);

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => ({
        x: Math.min(prev.x, window.innerWidth - PANEL_WIDTH),
        y: Math.min(prev.y, window.innerHeight - 100)
      }));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      ref={panelRef}
      className="fixed bg-white rounded-lg shadow-2xl z-50 flex flex-col"
      style={{
        left: position.x,
        top: position.y,
        width: `${PANEL_WIDTH}px`,
        maxWidth: '95vw',
        height: 'min(80vh, 700px)',
        cursor: isDragging ? 'grabbing' : 'default'
      }}
    >
      <div
        className="flex justify-between items-center p-3 border-b bg-blue-600 text-white rounded-t-lg cursor-grab active:cursor-grabbing select-none"
        onMouseDown={handleMouseDown}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">🎓</span>
          <h2 className="text-base font-bold">{title}</h2>
          <span className="text-xs text-blue-200 ml-2">⋮⋮ drag to move</span>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:text-blue-200 text-xl p-1 hover:bg-blue-700 rounded"
          title="Close tutorial"
        >
          ✕
        </button>
      </div>

      <div className="tutorial-content flex-1 overflow-y-auto p-4">
        {children}
      </div>

      <div className="border-t p-2 bg-gray-50 rounded-b-lg flex justify-between items-center">
        <span className="text-xs text-gray-500">Drag header to reposition</span>
        <button
          onClick={onClose}
          className="py-1 px-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-sm font-medium transition"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default DraggableTutorialPanel;
